/**
 * Sparse morph targets — ADR-038 §14.5 (J4): **rewrite chosen morph targets of a
 * written GLB as glTF SPARSE accessors, and repack the binary chunk.**
 *
 * three's GLTFExporter writes every morph target DENSE: a full VEC3 per vertex
 * per attribute, even where the target moves nothing. A surface-jiggle mode
 * moves one soft mass (tens to hundreds of vertices of tens of thousands), and
 * carries a NORMAL delta beside its POSITION delta, so fifteen dense modes on a
 * 50 k-vertex body would add ~18 MB to a file whose useful payload is ~0.5 MB.
 *
 * ONE responsibility: for every primitive target whose NAME (the mesh's
 * `extras.targetNames`, which GLTFExporter writes) the predicate selects, each
 * attribute accessor becomes `{ count, sparse: { indices, values } }` with no
 * base bufferView — the spec's all-zero base (glTF 2.0 §3.6.2.3), which is
 * exactly a relative morph target's rest. The bufferViews nothing references
 * any more are dropped and the BIN is repacked, 4-byte aligned. POSITION keeps
 * its min/max (the spec requires them; the values are unchanged). Bytes in,
 * bytes out: engine-free, no three.
 *
 * Depends on: nothing. Depended on by: export-glb.
 */

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;
const FLOAT = 5126;
const UNSIGNED_INT = 5125;

type Json = Record<string, unknown>;
interface Accessor {
  bufferView?: number;
  byteOffset?: number;
  componentType: number;
  count: number;
  type: string;
  normalized?: boolean;
  min?: number[];
  max?: number[];
  sparse?: { count: number; indices: { bufferView: number; byteOffset?: number; componentType: number }; values: { bufferView: number; byteOffset?: number } };
}
interface BufferView {
  buffer: number;
  byteOffset?: number;
  byteLength: number;
  byteStride?: number;
  target?: number;
}

export interface SparsifyReport {
  /** Target attributes rewritten sparse. */
  readonly accessors: number;
  /** BIN bytes before and after. */
  readonly binBefore: number;
  readonly binAfter: number;
}

export class SparseMorphError extends Error {}

export function sparsifyMorphTargets(glb: ArrayBuffer, select: (targetName: string) => boolean): { glb: ArrayBuffer; report: SparsifyReport } {
  const { json, bin } = parseGlb(glb);
  const accessors = (json['accessors'] ?? []) as Accessor[];
  const views = (json['bufferViews'] ?? []) as BufferView[];
  const meshes = (json['meshes'] ?? []) as { primitives: { targets?: Record<string, number>[] }[]; extras?: { targetNames?: string[] } }[];
  const appended: Uint8Array[] = [];
  const newViews: BufferView[] = [];
  const addView = (bytes: Uint8Array): number => {
    newViews.push({ buffer: 0, byteOffset: -1, byteLength: bytes.byteLength });
    appended.push(bytes);
    return views.length + newViews.length - 1;
  };
  const done = new Set<number>();
  let rewritten = 0;
  for (const mesh of meshes) {
    const names = mesh.extras?.targetNames ?? [];
    for (const prim of mesh.primitives) {
      (prim.targets ?? []).forEach((target, t) => {
        const name = names[t];
        if (name === undefined || !select(name)) return;
        for (const index of Object.values(target)) {
          if (done.has(index)) continue;
          const a = accessors[index];
          if (a === undefined || a.sparse !== undefined || a.bufferView === undefined) continue;
          if (a.componentType !== FLOAT || a.type !== 'VEC3' || a.normalized === true) {
            throw new SparseMorphError(`target "${name}": accessor ${index} is ${a.type}/${a.componentType}, not a float VEC3`);
          }
          const view = views[a.bufferView]!;
          const stride = view.byteStride ?? 12;
          const base = bin.byteOffset + (view.byteOffset ?? 0) + (a.byteOffset ?? 0);
          const dv = new DataView(bin.buffer, base, stride * (a.count - 1) + 12);
          const idx: number[] = [];
          const val: number[] = [];
          for (let v = 0; v < a.count; v++) {
            const x = dv.getFloat32(v * stride, true);
            const y = dv.getFloat32(v * stride + 4, true);
            const z = dv.getFloat32(v * stride + 8, true);
            if (x !== 0 || y !== 0 || z !== 0) {
              idx.push(v);
              val.push(x, y, z);
            }
          }
          delete a.bufferView;
          delete a.byteOffset;
          if (idx.length > 0) {
            const indices = addView(new Uint8Array(Uint32Array.from(idx).buffer));
            const values = addView(new Uint8Array(Float32Array.from(val).buffer));
            a.sparse = { count: idx.length, indices: { bufferView: indices, componentType: UNSIGNED_INT }, values: { bufferView: values } };
          }
          done.add(index);
          rewritten++;
        }
      });
    }
  }
  if (rewritten === 0) return { glb, report: { accessors: 0, binBefore: bin.byteLength, binAfter: bin.byteLength } };

  // Repack: keep every bufferView something still references, in order, 4-byte aligned.
  const allViews = [...views, ...newViews];
  const used = new Set<number>();
  for (const a of accessors) {
    if (a.bufferView !== undefined) used.add(a.bufferView);
    if (a.sparse !== undefined) {
      used.add(a.sparse.indices.bufferView);
      used.add(a.sparse.values.bufferView);
    }
  }
  for (const img of (json['images'] ?? []) as { bufferView?: number }[]) if (img.bufferView !== undefined) used.add(img.bufferView);
  const remap = new Map<number, number>();
  const kept: BufferView[] = [];
  const chunks: Uint8Array[] = [];
  let cursor = 0;
  allViews.forEach((view, i) => {
    if (!used.has(i)) return;
    const bytes = i < views.length ? bin.subarray(view.byteOffset ?? 0, (view.byteOffset ?? 0) + view.byteLength) : appended[i - views.length]!;
    const pad = (4 - (cursor % 4)) % 4;
    if (pad > 0) {
      chunks.push(new Uint8Array(pad));
      cursor += pad;
    }
    remap.set(i, kept.length);
    kept.push({ ...view, byteOffset: cursor });
    chunks.push(bytes);
    cursor += bytes.byteLength;
  });
  const out = new Uint8Array(cursor);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.byteLength;
  }
  for (const a of accessors) {
    if (a.bufferView !== undefined) a.bufferView = remap.get(a.bufferView)!;
    if (a.sparse !== undefined) {
      a.sparse.indices.bufferView = remap.get(a.sparse.indices.bufferView)!;
      a.sparse.values.bufferView = remap.get(a.sparse.values.bufferView)!;
    }
  }
  for (const img of (json['images'] ?? []) as { bufferView?: number }[]) if (img.bufferView !== undefined) img.bufferView = remap.get(img.bufferView)!;
  json['bufferViews'] = kept;
  const buffers = (json['buffers'] ?? []) as { byteLength: number }[];
  if (buffers[0] !== undefined) buffers[0].byteLength = out.byteLength;
  return { glb: buildGlb(json, out), report: { accessors: rewritten, binBefore: bin.byteLength, binAfter: out.byteLength } };
}

function parseGlb(glb: ArrayBuffer): { json: Json; bin: Uint8Array } {
  const view = new DataView(glb);
  if (glb.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC) throw new SparseMorphError('not a GLB (bad magic)');
  let cursor = 12;
  let json: Json | null = null;
  let bin: Uint8Array = new Uint8Array(0);
  while (cursor + 8 <= glb.byteLength) {
    const length = view.getUint32(cursor, true);
    const type = view.getUint32(cursor + 4, true);
    const data = new Uint8Array(glb, cursor + 8, length);
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(data)) as Json;
    else if (type === CHUNK_BIN) bin = data;
    cursor += 8 + length + ((4 - (length % 4)) % 4);
  }
  if (json === null) throw new SparseMorphError('the GLB has no JSON chunk');
  if ((json['buffers'] as unknown[] | undefined)?.length !== undefined && (json['buffers'] as unknown[]).length > 1) {
    throw new SparseMorphError('the GLB declares more than one buffer; only the embedded BIN is supported');
  }
  return { json, bin };
}

function buildGlb(json: Json, bin: Uint8Array): ArrayBuffer {
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPad = (4 - (jsonBytes.length % 4)) % 4;
  const binPad = (4 - (bin.length % 4)) % 4;
  const total = 12 + 8 + jsonBytes.length + jsonPad + (bin.length > 0 ? 8 + bin.length + binPad : 0);
  const out = new ArrayBuffer(total);
  const dv = new DataView(out);
  const u8 = new Uint8Array(out);
  dv.setUint32(0, GLB_MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, total, true);
  dv.setUint32(12, jsonBytes.length + jsonPad, true);
  dv.setUint32(16, CHUNK_JSON, true);
  u8.set(jsonBytes, 20);
  u8.fill(0x20, 20 + jsonBytes.length, 20 + jsonBytes.length + jsonPad);
  if (bin.length > 0) {
    const at = 20 + jsonBytes.length + jsonPad;
    dv.setUint32(at, bin.length + binPad, true);
    dv.setUint32(at + 4, CHUNK_BIN, true);
    u8.set(bin, at + 8);
  }
  return out;
}
